import { Controller } from "stimulus";

const savedValue = (input) => input.dataset.savedLogin ?? input.dataset.savedEmail ?? "";
const cleared = (input) => input.dataset.savedLogin !== undefined && input.value.trim().length < 1;

export default class extends Controller {
  static targets = ["credentialCatalog", "form", "modal", "required", "warning"];

  warnOrSubmit() {
    if (this.missingFieldPresent()) {
      this.highlightBlankRequiredFields();
    } else if (this.credentialWarningNeeded()) {
      this.openCredentialConfirmation();
    } else {
      this.formTarget.requestSubmit();
    }
  }

  completeSubmit() {
    this.formTarget.requestSubmit();
  }

  missingFieldPresent() {
    return this.requiredTargets.some(this.blankButNecessary);
  }

  // A catalog's credentials are one set, so a blank field is only acceptable while the set sits
  // untouched. Editing any part of it means re-entering the API key, which is never stored here.
  blankButNecessary(field) {
    const catalogKey = field.closest(".catalog-form").dataset.catalogId;
    const catalog = document.getElementById(`${catalogKey}_connected`);
    const customPricing = document.getElementById(`${catalogKey}_custom_pricing`);
    const credentials = ["catalog_login", "catalog_email", "catalog_secret"]
      .map((name) => document.getElementById(`${catalogKey}_${name}`))
      .filter((input) => input !== null);

    const login = credentials.find((input) => input.id === `${catalogKey}_catalog_login`);
    const removing = login !== undefined && cleared(login) && !customPricing?.checked;

    if (catalog === null || !catalog.checked || removing || field.value.trim().length > 0) return false;

    const edited = credentials.some((input) => input.value.trim() !== savedValue(input));
    const unconfigured = credentials.every((input) => savedValue(input).length < 1);

    return edited || (unconfigured && customPricing !== null && customPricing.checked);
  }

  clearedLogins() {
    return this.requiredTargets.filter(cleared);
  }

  credentialWarningNeeded() {
    return this.warningTargets.some((element) => !element.checked) || this.clearedLogins().length > 0;
  }

  highlightBlankRequiredFields() {
    this.clearPreviousErrors();
    this.requiredTargets
      .filter(this.blankButNecessary)
      .forEach((field) => field.parentElement.querySelector(".required").classList.remove("hidden"));
  }

  clearPreviousErrors() {
    this.requiredTargets
      .filter((field) => !this.blankButNecessary(field))
      .forEach((field) => {
        field.parentElement.querySelector(".required").classList.add("hidden");
        const error = field.closest(".catalog").querySelector(".credential-error");
        error?.classList?.add("hidden");
      });
  }

  openCredentialConfirmation() {
    this.updateCredentialWarning();
    this.modalTarget.showModal();
  }

  updateCredentialWarning() {
    const catalogName = this.getCredentialCatalogName();
    this.credentialCatalogTargets.forEach((field) => {
      field.textContent = catalogName;
    });
  }

  getCredentialCatalogName() {
    // Running the list of warningCatalogNames through a set ensures we only count each catalog once
    const warningCatalogNames = [
      ...new Set(
        [...this.warningTargets.filter((element) => !element.checked), ...this.clearedLogins()].map(
          (element) => element.dataset.catalog,
        ),
      ),
    ];

    if (warningCatalogNames.length > 1) {
      return `${warningCatalogNames.length} Catalogs`;
    }

    return warningCatalogNames[0];
  }
}
